import { z } from 'zod';
import { messageKey } from './messages.js';
/**
 * Виды учётных данных (§7.6). Вид определяет, как секрет будет использован:
 * пароль VNC уходит в RFB-аутентификацию, RTSP — в URL потока для go2rtc.
 *
 * `ssh` и `ssh_key` разделены не для красоты: у них разный секрет (пароль
 * против PEM), разная длина, разная проверка при сохранении и разный способ
 * аутентификации на сервере. Один вид с полем «а это ключ» пришлось бы
 * разбирать в каждом месте, где секрет достают.
 */
export const CREDENTIAL_KINDS = ['vnc', 'rtsp', 'snmp', 'http_basic', 'ssh', 'ssh_key'];
export const credentialKindSchema = z.enum(CREDENTIAL_KINDS);
/** Службы узла, к которым привязываются учётные данные (§7.6). */
export const CREDENTIAL_SERVICES = ['vnc', 'rtsp', 'snmp', 'ssh'];
export const credentialServiceSchema = z.enum(CREDENTIAL_SERVICES);
/** Какие виды учётных данных подходят каждой службе узла. */
export const CREDENTIAL_KINDS_BY_SERVICE = {
    vnc: ['vnc'],
    rtsp: ['rtsp'],
    snmp: ['snmp'],
    ssh: ['ssh', 'ssh_key'],
};
/**
 * Предел длины секрета по видам.
 *
 * Приватный ключ на четыре тысячи бит в формате OpenSSH — это около трёх
 * килобайт, а зашифрованный парольной фразой ещё длиннее. Поднимать общий
 * предел до этих величин нельзя: «пароль» на шестнадцать килобайт — это
 * не пароль, а чья-то ошибка вставки, и отказать в ней надо сразу.
 */
export const CREDENTIAL_SECRET_MAX_LENGTH = 512;
export const CREDENTIAL_KEY_MAX_LENGTH = 16384;
export function credentialSecretMaxLength(kind) {
    return kind === 'ssh_key' ? CREDENTIAL_KEY_MAX_LENGTH : CREDENTIAL_SECRET_MAX_LENGTH;
}
/**
 * Сколько символов пароля VNC на самом деле участвуют в проверке.
 *
 * Ограничение самого протокола: VNC Authentication шифрует вызов ключом
 * из первых восьми байт пароля, остальные отбрасываются. Значит, пароль
 * `верныйПароль` и пароль `верныйПарольКоторыйНеТот` для сервера
 * неразличимы — об этом §5.0 требует предупредить прямо при вводе,
 * а не оставлять на «почему-то пустил».
 */
export const VNC_PASSWORD_EFFECTIVE_LENGTH = 8;
/**
 * Учётные данные без секрета — ровно то, что отдаётся наружу.
 *
 * Отдельной схемы «с секретом» в контрактах нет и быть не должно (§3.3):
 * расшифрованное значение живёт только внутри шлюза доступа в момент
 * подключения и в контракты не попадает даже как тип.
 */
export const credentialSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    kind: credentialKindSchema,
    username: z.string().nullable(),
    /**
     * Задана ли парольная фраза у приватного ключа.
     *
     * Сам секрет наружу не уходит никогда, но знать, есть ли фраза, интерфейсу
     * надо: без этого не отличить «ключ без фразы» от «фразу забыли ввести»,
     * а отличаются они тем, что во втором случае подключение не состоится.
     */
    hasPassphrase: z.boolean(),
    /** Версия мастер-ключа: видно, что осталось перешифровать (§10.2). */
    keyVersion: z.number().int(),
    createdBy: z.uuid().nullable(),
    createdAt: z.iso.datetime(),
    /** К скольким узлам привязаны. Удалять привязанные нельзя. */
    usedByNodes: z.number().int(),
});
export const createCredentialSchema = z
    .object({
    name: z.string().trim().min(1, messageKey('validation.name.required')).max(100),
    kind: credentialKindSchema,
    /** У VNC Authentication имени пользователя нет; для остальных видов есть. */
    username: z.string().trim().max(100).nullish(),
    /**
     * Секрет. Пустой не принимаем: пустой пароль в хранилище неотличим
     * от «пароль не задан», а это разные вещи — во втором случае надо
     * отказать в подключении, а не молча пробовать пустой.
     *
     * У вида `ssh_key` здесь PEM приватного ключа, поэтому предел длины
     * зависит от вида и проверяется ниже.
     */
    secret: z
        .string()
        .min(1, messageKey('validation.secret.required'))
        .max(CREDENTIAL_KEY_MAX_LENGTH),
    /**
     * Парольная фраза приватного ключа. Только у `ssh_key` и только если
     * ключ ею зашифрован.
     */
    passphrase: z.string().min(1).max(CREDENTIAL_SECRET_MAX_LENGTH).nullish(),
})
    .superRefine((value, ctx) => {
    // У SSH имя пользователя — часть учётных данных, а не узла:
    // войти «паролем без пользователя» нельзя, а держать его
    // отдельно значит хранить одно значение в двух местах.
    if ((value.kind === 'ssh' || value.kind === 'ssh_key') &&
        (value.username ?? '').trim().length === 0) {
        ctx.addIssue({
            code: 'custom',
            path: ['username'],
            message: messageKey('validation.credential.sshUserRequired'),
        });
    }
    const limit = credentialSecretMaxLength(value.kind);
    if (value.secret.length > limit) {
        ctx.addIssue({
            code: 'custom',
            path: ['secret'],
            message: messageKey('validation.secret.tooLong', { max: limit }),
        });
    }
    // Фраза без ключа означала бы, что её негде применить: у пароля
    // и у RTSP-URL парольной фразы не бывает. Молча отбросить её нельзя —
    // человек решит, что она сохранена.
    if (value.passphrase != null && value.kind !== 'ssh_key') {
        ctx.addIssue({
            code: 'custom',
            path: ['passphrase'],
            message: messageKey('validation.credential.passphraseOnlyForKey'),
        });
    }
});
/**
 * Правка учётных данных.
 *
 * Вид (`kind`) не меняется: он определяет, как секрет будет использован —
 * пароль VNC уходит в RFB-аутентификацию, RTSP в URL потока, — и смена вида
 * у записи, уже привязанной к узлам, тихо сломала бы их подключения.
 * Нужен другой вид — заводится другая запись.
 *
 * Секрет необязателен: чаще правят название и имя пользователя. Переданный
 * секрет перешифровывается текущим мастер-ключом (§10.2), а прежний
 * затирается — прочитать его платформа не умеет и не должна.
 */
export const updateCredentialSchema = z
    .object({
    name: z.string().trim().min(1, messageKey('validation.name.required')).max(100),
    username: z.string().trim().max(100).nullish(),
    /**
     * Предел здесь общий, по самому длинному виду: `kind` в теле правки нет
     * (он не меняется), а брать его из базы схема не умеет. Точный предел
     * по виду записи проверяет `CredentialsService` — там строка уже
     * прочитана.
     */
    secret: z
        .string()
        .min(1, messageKey('validation.secret.required'))
        .max(CREDENTIAL_KEY_MAX_LENGTH),
    /**
     * Парольная фраза ключа. `null` — снять фразу с ключа, который её
     * больше не требует; отсутствие поля — не трогать.
     */
    passphrase: z.string().min(1).max(CREDENTIAL_SECRET_MAX_LENGTH).nullable(),
})
    .partial();
/** Привязка учётных данных к службе узла: `PUT /nodes/:id/credentials/:service`. */
export const attachCredentialSchema = z.object({
    credentialId: z.uuid(),
});
//# sourceMappingURL=credentials.js.map